import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  playPhaseStart, playWarning, playTimeUp, playClick,
  playCorrect, playWrong, playVictory, playRoundStart,
  playSpyReveal, playVote,
} from '../sounds';

const CANVAS_SIZE = 320;

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

// Shared canvas: loads previous turn's image as baseline, allows the active drawer
// to add strokes on top in their assigned color, undo their own additions, and
// submit the cumulative image.
function CollaborativeCanvas({ initialImage, color, onSubmit }) {
  const canvasRef = useRef(null);
  const [brushSize, setBrushSize] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [history, setHistory] = useState([]); // strokes the current player added
  const baselineRef = useRef(null); // dataURL of canvas before this turn began
  const lastPos = useRef(null);

  // Initialize canvas with previous turn's drawing as baseline
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFDFA';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const captureBaseline = () => {
      baselineRef.current = canvas.toDataURL();
      setHistory([baselineRef.current]);
    };

    if (initialImage) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        captureBaseline();
      };
      img.onerror = captureBaseline;
      img.src = initialImage;
    } else {
      captureBaseline();
    }
  }, [initialImage]);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    setHasDrawn(true);
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }, [getPos, brushSize, color]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  }, [isDrawing, getPos, color, brushSize]);

  const endDraw = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    lastPos.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      setHistory((prev) => [...prev.slice(-12), canvas.toDataURL()]);
    }
  }, [isDrawing]);

  const undo = () => {
    if (history.length <= 1) return;
    const newHistory = history.slice(0, -1);
    setHistory(newHistory);
    const img = new Image();
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = newHistory[newHistory.length - 1];
    if (newHistory.length === 1) setHasDrawn(false);
  };

  const reset = () => {
    if (!baselineRef.current) return;
    const img = new Image();
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = baselineRef.current;
    setHistory([baselineRef.current]);
    setHasDrawn(false);
  };

  const submit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL('image/png', 0.6);
    onSubmit(data);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{
          width: '100%', maxWidth: `${CANVAS_SIZE}px`, height: 'auto', aspectRatio: '1',
          borderRadius: '14px', border: '2px solid var(--terracotta-soft)',
          touchAction: 'none', cursor: 'crosshair', background: '#FFFDFA',
          boxShadow: 'var(--shadow-2)',
        }}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center',
        padding: '6px 12px', borderRadius: '999px',
        background: 'var(--surface-sunk)',
      }}>
        <span style={{
          width: '20px', height: '20px', borderRadius: '50%',
          background: color || '#000', border: '2px solid var(--ink-4)',
        }} />
        <span style={{ fontSize: '0.78rem', color: 'var(--ink-2)', fontWeight: 700 }}>
          הצבע שלך לכל המשחק
        </span>
      </div>

      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
        {[
          { size: 3, label: 'דק' },
          { size: 6, label: 'בינוני' },
          { size: 12, label: 'עבה' },
        ].map((b) => (
          <button
            key={b.size}
            onClick={() => setBrushSize(b.size)}
            className="btn btn-ghost"
            style={{
              padding: '6px 14px', fontSize: '0.8rem', fontWeight: 700,
              opacity: brushSize === b.size ? 1 : 0.45,
              background: brushSize === b.size ? 'var(--terracotta-wash)' : 'transparent',
            }}
          >{b.label}</button>
        ))}
        <button onClick={undo} disabled={history.length <= 1}
          className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
          ↩️ בטל
        </button>
        <button onClick={reset} disabled={history.length <= 1}
          className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
          🗑️ אפס
        </button>
      </div>

      <button
        className="btn btn-primary"
        onClick={submit}
        disabled={!hasDrawn}
        style={{ width: '100%', maxWidth: `${CANVAS_SIZE}px` }}
      >
        סיימתי - העבר תור ▶
      </button>
    </div>
  );
}

function FakeArtist({ gameState, playerId, sendMessage, error, clearError }) {
  const {
    phase, currentRound, totalRounds, phaseTimeLeft,
    isFake, isMyTurn, currentDrawerName, currentDrawerColor,
    myColor, category,
    turnIndex, totalTurns, turnsLeft, turnsPerPlayer, turnOrder,
    drawing, word, players,
    myVote, votedCount, fakeId, fakeName, votes,
    fakeCaught, fakeGuess, gameOver,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [guessInput, setGuessInput] = useState('');
  const isHost = players?.find((p) => p.id === playerId)?.isHost;
  const connectedPlayers = (players || []).filter((p) => p.connected !== false);

  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase, turnIndex]);

  useEffect(() => {
    if (phase === 'reveal' || phase === 'results') return;
    const interval = setInterval(() => {
      setLocalTimer((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, turnIndex]);

  useEffect(() => {
    if (phase === 'intro') {
      playRoundStart();
    } else if (phase === 'voting') {
      playVote();
    } else if (phase === 'reveal') {
      playSpyReveal();
      if (fakeCaught) setTimeout(playCorrect, 700);
      else setTimeout(playWrong, 700);
    } else if (phase === 'results') {
      playVictory();
    } else {
      playPhaseStart();
    }
  }, [phase, currentRound, fakeCaught]);

  // ===== INTRO PHASE =====
  if (phase === 'intro') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 3} />

        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
            סיבוב {currentRound} מתוך {totalRounds}
          </p>
        </div>

        {isFake ? (
          <div className="info-box spy">
            <div className="info-box-label">🎭 התפקיד שלך</div>
            <div className="info-box-text">
              אתה האמן <strong>המזויף</strong>!
              {category && (
                <>
                  <br />
                  <span style={{ fontSize: '0.85rem', opacity: 0.85 }}>הקטגוריה היא:</span>
                  <span style={{
                    display: 'inline-block', marginRight: '8px',
                    padding: '4px 12px', borderRadius: '999px',
                    background: 'var(--terracotta-soft)', color: 'var(--ink)',
                    fontSize: '1rem', fontWeight: 800,
                  }}>{category}</span>
                </>
              )}
              <br />
              <span style={{ fontSize: '0.92rem', fontWeight: 500, opacity: 0.95 }}>
                לא קיבלת את המילה המדויקת. תאלתר ציור שמשתלב.
                <br />
                אם לא יחשפו אותך - אתה מנצח!
              </span>
            </div>
          </div>
        ) : (
          <div className="info-box normal">
            <div className="info-box-label">🎨 המילה שלך</div>
            <div className="info-box-text">
              <span style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--terracotta-deep)', display: 'block', marginBottom: '6px' }}>
                {word}
              </span>
              {category && (
                <span style={{
                  display: 'inline-block', marginBottom: '8px',
                  padding: '3px 10px', borderRadius: '999px',
                  background: 'var(--surface-sunk)', color: 'var(--ink-2)',
                  fontSize: '0.78rem', fontWeight: 700,
                }}>קטגוריה: {category}</span>
              )}
              <br />
              <span style={{ fontSize: '0.88rem', fontWeight: 500 }}>
                ציירו פרט אחד מהמילה. אחד מכם הוא <strong>מזויף</strong> ולא יודע - גלו אותו!
              </span>
            </div>
          </div>
        )}

        {myColor && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '8px', marginTop: '12px',
            padding: '8px 14px', borderRadius: '999px',
            background: 'var(--surface-sunk)',
          }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: myColor, border: '2px solid var(--ink-4)',
            }} />
            <span style={{ fontSize: '0.82rem', color: 'var(--ink-2)', fontWeight: 700 }}>
              הצבע שלך לכל המשחק
            </span>
          </div>
        )}

        <div className="result-section" style={{ marginTop: '20px' }}>
          <h3>סדר התורים והצבעים</h3>
          <ul className="player-list" style={{ marginBottom: 0 }}>
            {(turnOrder || []).map((id, i) => {
              const p = players?.find((pl) => pl.id === id);
              const isMe = id === playerId;
              return (
                <li key={id} className="player-item" style={{ marginBottom: '6px' }}>
                  <span className="player-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      background: 'var(--terracotta-wash)', color: 'var(--terracotta-deep)',
                      width: '24px', height: '24px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.78rem', fontWeight: 800,
                    }}>{i + 1}</span>
                    <span style={{ fontSize: '1.05rem' }}>{p?.avatar || '👤'}</span>
                    <span>{p?.name || '?'}</span>
                    {isMe && <span className="me-badge">(אני)</span>}
                  </span>
                  {p?.color && (
                    <span style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: p.color, border: '2px solid var(--ink-4)',
                      flexShrink: 0,
                    }} title="הצבע שלו בקנבס" />
                  )}
                </li>
              );
            })}
          </ul>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-3)', marginTop: '10px', textAlign: 'center' }}>
            כל אחד יצייר {turnsPerPlayer || 2} פעמים בצבע שלו
          </p>
        </div>
      </div>
    );
  }

  // ===== DRAWING PHASE =====
  if (phase === 'drawing') {
    const myTurnPosition = (turnOrder || []).indexOf(playerId);
    const currentSweep = Math.floor(turnIndex / (turnOrder?.length || 1)) + 1;

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
            סיבוב {currentRound}/{totalRounds} • תור {turnIndex + 1}/{totalTurns} • סבב {currentSweep}/{turnsPerPlayer}
          </p>
        </div>

        {/* Word/role reminder strip */}
        {isFake ? (
          <div style={{
            textAlign: 'center', padding: '10px 14px', marginBottom: '14px',
            background: 'var(--ink)', color: 'var(--terracotta-soft)',
            borderRadius: 'var(--r-sm)', fontWeight: 700, fontSize: '0.9rem',
          }}>
            🎭 אתה המזויף - תאלתר משהו שמשתלב
          </div>
        ) : word ? (
          <div style={{
            textAlign: 'center', padding: '10px 14px', marginBottom: '14px',
            background: 'var(--terracotta-wash)', borderRadius: 'var(--r-sm)',
            border: '1px solid var(--terracotta-soft)',
          }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--ink-3)' }}>המילה: </span>
            <span style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--terracotta-deep)' }}>{word}</span>
          </div>
        ) : null}

        {isMyTurn ? (
          <>
            <h2 style={{ textAlign: 'center', fontSize: '1.15rem', marginBottom: '6px', color: 'var(--terracotta-deep)' }}>
              ✏️ התור שלך לצייר!
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--ink-2)', fontSize: '0.85rem', marginBottom: '14px' }}>
              {isFake ? 'אלתר משהו שיתאים לציור הקיים' : 'הוסף פרט אחד מהמילה לציור'}
            </p>
            <CollaborativeCanvas
              initialImage={drawing}
              color={myColor || '#2B2622'}
              onSubmit={(data) => {
                playClick();
                sendMessage('FAKEARTIST_DRAW', { imageData: data });
              }}
            />
          </>
        ) : (
          <>
            <h2 style={{ textAlign: 'center', fontSize: '1.1rem', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {currentDrawerColor && (
                <span style={{
                  width: '18px', height: '18px', borderRadius: '50%',
                  background: currentDrawerColor, border: '2px solid var(--ink-4)',
                  display: 'inline-block',
                }} />
              )}
              <span style={{ color: 'var(--terracotta-deep)' }}>{currentDrawerName}</span> מצייר/ת...
            </h2>
            {myTurnPosition >= 0 && (
              <p style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: '0.82rem', marginBottom: '14px' }}>
                התור שלך: מקום {myTurnPosition + 1} בסבב
              </p>
            )}
            {drawing ? (
              <img
                src={drawing}
                alt="ציור משותף"
                style={{
                  width: '100%', maxWidth: `${CANVAS_SIZE}px`, borderRadius: '14px',
                  display: 'block', margin: '0 auto 12px',
                  border: '2px solid var(--terracotta-soft)',
                  background: '#FFFDFA',
                  boxShadow: 'var(--shadow-2)',
                }}
              />
            ) : (
              <div style={{
                width: '100%', maxWidth: `${CANVAS_SIZE}px`, aspectRatio: '1',
                margin: '0 auto 12px', borderRadius: '14px',
                background: '#FFFDFA',
                border: '2px dashed var(--terracotta-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--ink-3)', fontSize: '0.9rem',
              }}>
                הציור עוד לא התחיל...
              </div>
            )}
          </>
        )}

        <p style={{
          textAlign: 'center', fontSize: '0.78rem', color: 'var(--ink-3)',
          marginTop: '6px', fontWeight: 600,
        }}>
          נותרו {turnsLeft} תורים
        </p>

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== VOTING PHASE =====
  if (phase === 'voting') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '6px', color: 'var(--ink)' }}>
          🕵️ מי האמן המזויף?
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--ink-2)', fontSize: '0.88rem', marginBottom: '14px' }}>
          הצביעו - מי לדעתכם לא ידע את המילה?
        </p>

        {drawing && (
          <img
            src={drawing}
            alt="הציור המשותף"
            style={{
              width: '100%', maxWidth: '240px', borderRadius: '12px',
              display: 'block', margin: '0 auto 14px',
              border: '2px solid var(--terracotta-soft)',
              background: '#FFFDFA',
              boxShadow: 'var(--shadow-2)',
            }}
          />
        )}

        {!isFake && word && (
          <div style={{
            textAlign: 'center', padding: '8px 12px', marginBottom: '14px',
            background: 'var(--terracotta-wash)', borderRadius: 'var(--r-sm)',
            border: '1px solid var(--terracotta-soft)',
          }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--ink-3)' }}>המילה הייתה: </span>
            <span style={{ fontSize: '1.05rem', fontWeight: 900, color: 'var(--terracotta-deep)' }}>{word}</span>
          </div>
        )}

        <ul className="player-list" style={{ marginBottom: '12px' }}>
          {connectedPlayers.filter((p) => p.id !== playerId).map((p) => {
            const selected = myVote === p.id;
            const dimmed = myVote && !selected;
            return (
              <li key={p.id}
                className="player-item"
                onClick={() => {
                  if (myVote) return;
                  playClick();
                  sendMessage('FAKEARTIST_VOTE', { suspectId: p.id });
                }}
                style={{
                  cursor: myVote ? 'default' : 'pointer',
                  background: selected ? 'var(--terracotta-wash)' : undefined,
                  border: selected ? '1.5px solid var(--terracotta)' : undefined,
                  opacity: dimmed ? 0.5 : 1,
                  marginBottom: '6px',
                  transition: 'all 0.15s',
                }}
              >
                <span className="player-name" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {p.color && (
                    <span style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: p.color, border: '2px solid var(--ink-4)',
                      flexShrink: 0,
                    }} aria-label={`הצבע של ${p.name}`} />
                  )}
                  <span style={{ fontSize: '1.3rem' }}>{p.avatar || '👤'}</span>
                  <span style={{ fontWeight: 700 }}>{p.name}</span>
                </span>
                {selected && (
                  <span style={{ color: 'var(--terracotta-deep)', fontWeight: 700, fontSize: '0.85rem' }}>
                    ✓ הצבעת
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <p style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: '0.85rem' }}>
          הצביעו: {votedCount || 0}/{connectedPlayers.length}
        </p>

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== REVEAL PHASE =====
  if (phase === 'reveal') {
    const fakePlayer = players?.find((p) => p.id === fakeId);
    const fakeWasMe = fakeId === playerId;

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{
          textAlign: 'center', fontSize: '1.4rem', marginBottom: '12px',
          color: fakeCaught ? 'var(--success)' : 'var(--danger)',
          fontWeight: 900,
        }}>
          {fakeCaught ? '🎉 המזויף נתפס!' : '🎭 המזויף ברח!'}
        </h2>

        <div className="info-box spy">
          <div className="info-box-label">האמן המזויף היה</div>
          <div className="info-box-text" style={{ fontSize: '1.4rem' }}>
            {fakePlayer?.avatar || '🎭'} {fakeName}
            {fakeWasMe && <span className="me-badge" style={{ marginRight: '8px' }}>(אני)</span>}
          </div>
        </div>

        <div className="info-box normal">
          <div className="info-box-label">המילה הייתה</div>
          <div className="info-box-text" style={{ fontSize: '1.6rem', color: 'var(--terracotta-deep)' }}>
            {word}
          </div>
        </div>

        {drawing && (
          <img
            src={drawing}
            alt="הציור הסופי"
            style={{
              width: '100%', maxWidth: '220px', borderRadius: '12px',
              display: 'block', margin: '0 auto 14px',
              border: '2px solid var(--terracotta-soft)',
              background: '#FFFDFA',
              boxShadow: 'var(--shadow-2)',
            }}
          />
        )}

        {/* Vote breakdown */}
        <div className="result-section">
          <h3>הצבעות</h3>
          <ul className="player-list" style={{ marginBottom: 0 }}>
            {Object.entries(votes || {}).map(([voterId, suspectId]) => {
              const voter = players?.find((p) => p.id === voterId);
              const suspect = players?.find((p) => p.id === suspectId);
              const isFakeVote = suspectId === fakeId;
              return (
                <li key={voterId} className="player-item" style={{ marginBottom: '4px' }}>
                  <span className="player-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {voter?.color && (
                      <span style={{
                        width: '14px', height: '14px', borderRadius: '50%',
                        background: voter.color, border: '1.5px solid var(--ink-4)', flexShrink: 0,
                      }} />
                    )}
                    <span>{voter?.avatar || '👤'}</span>
                    <span>{voter?.name}</span>
                  </span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--ink-2)' }}>
                    →{' '}
                    <strong style={{ color: isFakeVote ? 'var(--success)' : 'var(--ink)' }}>
                      {suspect?.name} {isFakeVote ? '✓' : ''}
                    </strong>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Fake's chance to guess the word for bonus */}
        {fakeCaught && isFake && !fakeGuess && (
          <div className="info-box warning">
            <div className="info-box-label">🎯 הזדמנות אחרונה</div>
            <div className="info-box-text" style={{ fontSize: '0.95rem' }}>
              נחש את המילה לבונוס של 5 נקודות
            </div>
            <input
              className="input"
              placeholder="מה הייתה המילה?"
              value={guessInput}
              onChange={(e) => setGuessInput(e.target.value)}
              maxLength={50}
              style={{ marginTop: '10px', marginBottom: '8px' }}
            />
            <button
              className="btn btn-primary"
              onClick={() => {
                if (guessInput.trim()) {
                  playClick();
                  sendMessage('FAKEARTIST_GUESS', { guess: guessInput.trim() });
                  setGuessInput('');
                }
              }}
              disabled={!guessInput.trim()}
              style={{ width: '100%' }}
            >
              שלח ניחוש
            </button>
          </div>
        )}

        {fakeGuess && (
          <div className={`info-box ${fakeGuess.correct ? 'question' : 'warning'}`}>
            <div className="info-box-label">ניחוש המזויף</div>
            <div className="info-box-text" style={{
              color: fakeGuess.correct ? 'var(--success)' : 'var(--danger)',
            }}>
              "{fakeGuess.text}" {fakeGuess.correct ? '✓ נכון! +5' : '✗'}
            </div>
          </div>
        )}

        {/* Scoreboard */}
        <div className="result-section">
          <h3>טבלת ניקוד</h3>
          <div className="leaderboard">
            {[...(players || [])].sort((a, b) => b.score - a.score).map((p, i) => (
              <div key={p.id} className="leaderboard-item">
                <span className="leaderboard-rank">
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>}{p.name}
                  {p.id === fakeId && <span style={{ color: 'var(--danger)', marginRight: '6px' }} title="היה המזויף">🎭</span>}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {!gameOver && isHost && (
          <button
            className="btn btn-primary"
            onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}
            style={{ width: '100%' }}
          >
            סיבוב הבא ▶
          </button>
        )}
        {!gameOver && !isHost && <p className="waiting">ממתינים שהמנהל ימשיך...</p>}

        {gameOver && isHost && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => { clearError(); sendMessage('START_GAME', {}); }}
              style={{ width: '100%', marginBottom: '8px' }}
            >שחקו שוב 🔄</button>
            <button
              className="btn btn-secondary"
              onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}
              style={{ width: '100%' }}
            >חזרה ללובי</button>
          </>
        )}
        {gameOver && !isHost && <p className="waiting">ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== RESULTS / FINAL =====
  if (phase === 'results') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{ textAlign: 'center', fontSize: '1.4rem', marginBottom: '20px', color: 'var(--ink)' }}>
          🏆 תוצאות סופיות
        </h2>
        <div className="leaderboard">
          {[...(players || [])].sort((a, b) => b.score - a.score).map((p, i) => (
            <div key={p.id} className="leaderboard-item">
              <span className="leaderboard-rank">
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
              </span>
              <span className="leaderboard-name">
                {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>}{p.name}
                {p.id === playerId && <span className="me-badge"> (אני)</span>}
              </span>
              <span className="leaderboard-score">{p.score}</span>
            </div>
          ))}
        </div>
        {isHost && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => sendMessage('START_GAME', {})}
              style={{ width: '100%', marginTop: '16px', marginBottom: '8px' }}
            >שחקו שוב</button>
            <button
              className="btn btn-secondary"
              onClick={() => sendMessage('NEW_GAME', {})}
              style={{ width: '100%' }}
            >חזרה ללובי</button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <p style={{ textAlign: 'center', color: 'var(--ink-2)' }}>טוען...</p>
    </div>
  );
}

export default FakeArtist;
